import React, { type ComponentProps } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { ScrollView, StyleSheet, View } from 'react-native';

import { FONTS, theme } from '@garnish/constants';
import { BodyText, Text, useResponsive } from '@garnish/core';
import { ContentfulImage, type ContentfulImageTransformOptions } from '@garnish/media';

import { BagFeaturedUpsellAddButton } from '../BagFeaturedUpsellAddButton';
import { BagFeaturedUpsellButton } from '../BagFeaturedUpsellButton';
import { BagFeaturedUpsellReveal } from '../BagFeaturedUpsellReveal';

// ─────────────────────────────────────────────────────────────────────────────

export const BagFeaturedUpsellCard = (props: Props) => {
  const { content, addCta, isAdding, isAdded, onAdd, onDismiss } = props;
  const { imageUrl, imageA11y, eyebrow, headline, badgeImageUrl, badgeImageA11y, dismissCta } =
    content;

  const { currentBreakpoint, match } = useResponsive();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isCompact = currentBreakpoint.isXS;
  const addStatus = getAddStatus({ isAdding, isAdded });
  const [firstHeadlineLine, ...nextHeadlineLines] = headline.split('\n');
  const badgeEnterOrder = nextHeadlineLines.length + 2;
  const actionsEnterOrder = badgeEnterOrder + 1;
  const addEnterOrder = isCompact ? actionsEnterOrder : actionsEnterOrder + 1;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const actionsStyles = match([styles.actionsXS, styles.actionsSM]);
  const actionStyles = match([undefined, styles.actionSM]);
  const accentGradient = match([ACCENT_GRADIENT_XS, ACCENT_GRADIENT_SM]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
      <ContentfulImage
        baseUrl={imageUrl}
        options={IMAGE_OPTIONS}
        alt={imageA11y}
        contentFit="cover"
        style={styles.image}
      />

      <LinearGradient
        colors={TOP_GRADIENT_COLORS}
        locations={TOP_GRADIENT_LOCATIONS}
        pointerEvents="none"
        style={StyleSheet.absoluteFill}
      />

      <LinearGradient
        colors={ACCENT_GRADIENT_COLORS}
        locations={ACCENT_GRADIENT_LOCATIONS}
        start={accentGradient.start}
        end={accentGradient.end}
        pointerEvents="none"
        style={StyleSheet.absoluteFill}
      />

      <ScrollView
        bounces={false}
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.content}
      >
        {eyebrow ? (
          <BagFeaturedUpsellReveal order={0}>
            <BodyText sizeMatch={['18']} style={styles.eyebrow}>
              {eyebrow}
            </BodyText>
          </BagFeaturedUpsellReveal>
        ) : null}

        <View style={styles.headlineContainer}>
          <BagFeaturedUpsellReveal order={1}>
            <Text bold family={FONTS.SWEET_SANS_CONDENSED} style={styles.headline}>
              {firstHeadlineLine}
            </Text>

            {badgeImageUrl ? (
              <View style={styles.badge}>
                <BagFeaturedUpsellReveal order={badgeEnterOrder} style={styles.badgeImage}>
                  <ContentfulImage
                    baseUrl={badgeImageUrl}
                    options={BADGE_IMAGE_OPTIONS}
                    alt={badgeImageA11y}
                    contentFit="contain"
                    style={styles.badgeImage}
                  />
                </BagFeaturedUpsellReveal>
              </View>
            ) : null}
          </BagFeaturedUpsellReveal>

          {nextHeadlineLines.map((line, index) => (
            <BagFeaturedUpsellReveal key={index} order={index + 2} style={styles.nextHeadlineLine}>
              <Text bold family={FONTS.SWEET_SANS_CONDENSED} style={styles.headline}>
                {line}
              </Text>
            </BagFeaturedUpsellReveal>
          ))}
        </View>

        <View style={actionsStyles}>
          {isCompact ? null : (
            <BagFeaturedUpsellReveal order={actionsEnterOrder} style={actionStyles}>
              <BagFeaturedUpsellButton palette="outline" label={dismissCta} onPress={onDismiss} />
            </BagFeaturedUpsellReveal>
          )}

          <BagFeaturedUpsellReveal order={addEnterOrder} style={actionStyles}>
            <BagFeaturedUpsellAddButton status={addStatus} label={addCta} onPress={onAdd} />
          </BagFeaturedUpsellReveal>

          {isCompact ? (
            <BagFeaturedUpsellReveal order={actionsEnterOrder + 1}>
              <BagFeaturedUpsellButton palette="link" label={dismissCta} onPress={onDismiss} />
            </BagFeaturedUpsellReveal>
          ) : null}
        </View>
      </ScrollView>
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAddStatus(params: GetAddStatusParams): AddStatus {
  const { isAdding, isAdded } = params;

  if (isAdded) {
    return 'added';
  }

  if (isAdding) {
    return 'adding';
  }

  return 'idle';
}

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_OPTIONS: Partial<ContentfulImageTransformOptions> = { fm: 'webp', w: 600 };
const BADGE_IMAGE_OPTIONS: Partial<ContentfulImageTransformOptions> = { fm: 'webp', w: 54 };

const TOP_GRADIENT_COLORS = ['rgba(20, 6, 0, 0.66)', 'rgba(20, 6, 0, 0)'] as const;
const TOP_GRADIENT_LOCATIONS = [0.01, 0.24] as const;

const ACCENT_GRADIENT_COLORS = ['rgba(20, 6, 0, 0)', 'rgba(20, 6, 0, 0.66)'] as const;
const ACCENT_GRADIENT_LOCATIONS = [0.59, 0.85] as const;

const ACCENT_GRADIENT_XS = { start: { x: 0.87, y: -0.03 }, end: { x: 0.13, y: 1.03 } };
const ACCENT_GRADIENT_SM = { start: { x: 0.64, y: -0.09 }, end: { x: 0.36, y: 1.09 } };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  image: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.DARK_KALE,
  },
  scrollView: {
    flexGrow: 0,
  },
  content: {
    minHeight: 565,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['6'],
    paddingVertical: theme.spacing['6'],
  },
  eyebrow: {
    color: theme.colors.OATMEAL,
    textAlign: 'center',
    letterSpacing: 0.54,
  },
  headlineContainer: {
    alignItems: 'center',
    maxWidth: '100%',
  },
  headline: {
    color: theme.colors.OATMEAL,
    fontSize: 60,
    lineHeight: 60,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  nextHeadlineLine: {
    marginTop: -14,
  },
  badge: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 54,
    height: 54,
    transform: [{ translateX: '63%' }, { translateY: '-50%' }, { rotate: '-8.4deg' }],
  },
  badgeImage: {
    width: '100%',
    height: '100%',
  },
  actionsXS: {
    alignSelf: 'stretch',
    gap: theme.spacing['6'],
    paddingHorizontal: theme.spacing['4'],
  },
  actionsSM: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    gap: theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
  },
  actionSM: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  content: Content;
  addCta: string;
  isAdding: boolean;
  isAdded: boolean;
  onAdd: () => void;
  onDismiss: () => void;
};

type GetAddStatusParams = Pick<Props, 'isAdding' | 'isAdded'>;

type AddStatus = ComponentProps<typeof BagFeaturedUpsellAddButton>['status'];

type Content = {
  imageUrl: string;
  imageA11y?: string;
  eyebrow?: string;
  headline: string;
  badgeImageUrl?: string;
  badgeImageA11y?: string;
  dismissCta: string;
};
